import { DebouncedSearchInput } from '@lidea/shared/ui/core';
import { Checkbox, ScrollArea, Stack, Text } from '@mantine/core';
import classNames from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import actions from './modal-actions.module.css';
import styles from './pf-typeahead.module.css';

export type TypeaheadItem = {
  value: string | number;
  label: string;
};

export type PfTypeaheadMode = 'multi' | 'single';

export interface PfTypeaheadProps {
  items: TypeaheadItem[];
  selectedItems: string[];
  limit?: number;
  /** `single` = click to pick one row (forms); `multi` = checkboxes (filters). */
  mode?: PfTypeaheadMode;
  /** Show search field (default: multi-select, or when explicitly enabled for long single lists). */
  showSearch?: boolean;
  /** Shorter layout when embedded under a form field (avoids nested IonModal). */
  compact?: boolean;
  onSelectionChange?: (items: string[]) => void;
}

export function PfTypeahead({
  items,
  selectedItems,
  limit,
  mode = 'multi',
  showSearch,
  compact = false,
  onSelectionChange,
}: PfTypeaheadProps) {
  const { t } = useTranslation(['common'], { useSuspense: false });
  const safeItems = items ?? [];
  const [filterQuery, setFilterQuery] = useState('');
  const [workingSelected, setWorkingSelected] = useState(() =>
    (selectedItems ?? []).map(String)
  );

  const handleFilterQuery = useCallback((value: string) => {
    setFilterQuery(value);
  }, []);

  const filteredItems = useMemo(() => {
    const query = filterQuery.trim().toLowerCase();
    if (!query) {
      return safeItems;
    }
    return safeItems.filter((item) =>
      (item.label ?? '').toLowerCase().includes(query)
    );
  }, [safeItems, filterQuery]);

  const atLimit = limit !== undefined && workingSelected.length >= limit;

  const toggleValue = useCallback(
    (value: string, checked: boolean) => {
      if (checked) {
        if (atLimit && !workingSelected.includes(value)) {
          return;
        }
        setWorkingSelected((current) =>
          current.includes(value) ? current : [...current, value]
        );
        return;
      }
      setWorkingSelected((current) =>
        current.filter((item) => item !== value)
      );
    },
    [atLimit, workingSelected]
  );

  const confirm = () => onSelectionChange?.(workingSelected);
  const reset = () => onSelectionChange?.([]);

  const pickSingle = (value: string) => {
    onSelectionChange?.([value]);
  };

  const isSingle = mode === 'single';
  const searchEnabled = showSearch ?? !isSingle;

  return (
    <div
      className={classNames(styles.panel, {
        [styles.panelCompact]: compact,
      })}
    >
      {searchEnabled ? (
        <DebouncedSearchInput
          className={styles.search}
          onDebouncedChange={handleFilterQuery}
        />
      ) : null}

      {!isSingle && workingSelected.length > 0 ? (
        <Text className={styles.selectionHint} size="sm">
          {t('common:filters_selected_count', {
            count: workingSelected.length,
          })}
        </Text>
      ) : null}

      <ScrollArea className={styles.list} offsetScrollbars type="auto">
        {filteredItems.length === 0 ? (
          <Text className={styles.empty} c="dimmed" size="sm">
            {t('common:no_results')}
          </Text>
        ) : (
          <Stack className={styles.options} gap={4}>
            {filteredItems.map((item) => {
              const value = String(item.value);
              const checked = workingSelected.includes(value);
              const disabled = !isSingle && !checked && atLimit;

              if (isSingle) {
                return (
                  <button
                    className={classNames(styles.option, styles.optionButton, {
                      [styles.optionChecked]: checked,
                    })}
                    key={value}
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      pickSingle(value);
                    }}
                  >
                    <span className={styles.optionLabel}>{item.label}</span>
                  </button>
                );
              }

              return (
                <div
                  className={classNames(styles.option, {
                    [styles.optionChecked]: checked,
                    [styles.optionDisabled]: disabled,
                  })}
                  key={value}
                >
                  <Checkbox
                    checked={checked}
                    classNames={{ label: styles.optionLabel }}
                    color="green"
                    data-testid={value}
                    disabled={disabled}
                    label={item.label}
                    radius="sm"
                    size="md"
                    onChange={(event) =>
                      toggleValue(value, event.currentTarget.checked)
                    }
                  />
                </div>
              );
            })}
          </Stack>
        )}
      </ScrollArea>

      {!isSingle ? (
        <div className={actions.actionsStacked}>
          <button
            className={actions.btnFilled}
            type="button"
            onClick={confirm}
          >
            {t('common:validate')}
          </button>
          <button className={actions.btnReset} type="button" onClick={reset}>
            {t('common:reset')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default PfTypeahead;
